import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '@/api/ops';
import type { BillingProfile, Invoice, ManualGrantResult, PriceOverride, Purchase } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select, Segmented } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { CreditAmount } from '@/components/ops/CreditAmount';
import { ReasonField, TextAreaField, TextField, reasonOk } from '@/components/ops/Forms';
import { KeyValues, LoadMore, Section } from '@/components/ops/Layout';
import { ConfirmDialog, Modal } from '@/components/ops/Modal';
import { MonthSelect } from '@/components/ops/Pickers';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatCredits, parseDisplayedCredits, scaleText } from '@/ops/credits';
import { currentPeriod, formatBps, formatMinor, formatTimestamp, parseMajorToMinor, shortId } from '@/ops/format';
import { newIdempotencyKey } from '@/ops/ids';
import { LIVE } from '@/ops/live';
import { scaleUnavailableReason, useCreditScale } from '@/ops/useCreditScale';

const key = (env: string, tenantId: string, what: string) => ['ops', 'billing', env, what, tenantId] as const;

/** The message-price families a tenant override can target (CREDITS SKUs, money review C10). */
export const MESSAGE_SKU_FAMILIES = {
  sms: { label: 'SMS (per part)', prefix: 'sms.part', types: ['transactional', 'marketing', 'otp'] },
  whatsapp: { label: 'WhatsApp (per message)', prefix: 'whatsapp.message', types: ['marketing', 'utility', 'authentication', 'service'] },
  verify: { label: 'Provider verification', prefix: 'sms.verify', types: ['sms', 'whatsapp'] },
} as const;

export type MessageSkuFamily = keyof typeof MESSAGE_SKU_FAMILIES;

/**
 * A message-type SKU: `sms.part.<purpose>.<CC|*>`, `whatsapp.message.<category>.<CC|*>` or `sms.verify.<channel>.<CC|*>`.
 * Null when the country is neither `*` nor two letters (ISO 3166) or the type is not one of the family's.
 */
export function messageSku(family: MessageSkuFamily, type: string, country: string): string | null {
  const spec = MESSAGE_SKU_FAMILIES[family];
  const cc = country.trim() === '' ? '*' : country.trim().toUpperCase();
  if (!(spec.types as readonly string[]).includes(type)) return null;
  if (cc !== '*' && !/^[A-Z]{2}$/.test(cc)) return null;
  return `${spec.prefix}.${type}.${cc}`;
}

/**
 * The billing half of the tenant Billing tab (plan §6.6): billing profile, price overrides, invoices,
 * payments, purchases (+ retry of a parked grant) and the manual grant. Every figure is a LIVE call
 * through the console's billing proxy; every write carries the acting admin (stamped by the proxy) and a
 * reason (CP5).
 */
export function BillingAdminSections({ env, tenantId }: { env: string; tenantId: string }) {
  return (
    <>
      <ProfileSection env={env} tenantId={tenantId} />
      <OverridesSection env={env} tenantId={tenantId} />
      <InvoicesSection env={env} tenantId={tenantId} />
      <PurchasesSection env={env} tenantId={tenantId} />
      <PaymentsSection env={env} tenantId={tenantId} />
      <ManualGrantSection env={env} tenantId={tenantId} />
    </>
  );
}

// ─── Billing profile ──────────────────────────────────────────────────────

function ProfileSection({ env, tenantId }: { env: string; tenantId: string }) {
  const profile = useQuery({ queryKey: key(env, tenantId, 'profile'), queryFn: () => billingApi.profile(env, tenantId), ...LIVE });
  const [editing, setEditing] = useState(false);
  const canManage = hasPermission(PERMISSIONS.billingManage);

  return (
    <Section title="Billing profile" meta="currency, tax and legal identity — overrides of the platform defaults"
      actions={canManage && profile.data && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Edit</button>}>
      <QueryState query={profile} service="billing">
        {(p) => (
          <KeyValues rows={[
            { label: 'Currency', value: <>{p.effective_currency} <span className="t-caption muted">{p.currency ? 'override' : 'platform default'}</span></> },
            { label: 'Tax rate', value: <>{formatBps(p.effective_tax_rate_bps)} <span className="t-caption muted">{p.tax_rate_bps !== null ? 'override' : 'platform default'}</span></> },
            { label: 'Tax exempt reason', value: p.tax_exempt_reason ?? '—' },
            { label: 'Tax registration id', value: p.tax_registration_id ?? '—' },
            { label: 'Legal name', value: p.legal_name ?? '—' },
            { label: 'Address', value: p.address ? <pre className="pre-json">{JSON.stringify(p.address, null, 2)}</pre> : '—' },
            { label: 'Invoice language', value: p.invoice_language },
            { label: 'Payment terms', value: `${p.payment_terms_days} days` },
            { label: 'Notify users', value: p.notify_user_ids.length ? p.notify_user_ids.map((id) => <div key={id} className="mono t-caption">{id}</div>) : '—' },
            { label: 'Updated', value: formatTimestamp(p.updated_at) },
          ]} />
        )}
      </QueryState>
      {editing && profile.data && <ProfileForm env={env} tenantId={tenantId} profile={profile.data} onClose={() => setEditing(false)} />}
    </Section>
  );
}

function ProfileForm({ env, tenantId, profile, onClose }: { env: string; tenantId: string; profile: BillingProfile; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [currency, setCurrency] = useState(profile.currency ?? '');
  const [taxPercent, setTaxPercent] = useState(profile.tax_rate_bps === null ? '' : String(profile.tax_rate_bps / 100));
  const [exemptReason, setExemptReason] = useState(profile.tax_exempt_reason ?? '');
  const [taxId, setTaxId] = useState(profile.tax_registration_id ?? '');
  const [legalName, setLegalName] = useState(profile.legal_name ?? '');
  const [address, setAddress] = useState(profile.address ? JSON.stringify(profile.address, null, 2) : '');
  const [language, setLanguage] = useState<'en' | 'ar'>(profile.invoice_language === 'ar' ? 'ar' : 'en');
  const [terms, setTerms] = useState(String(profile.payment_terms_days));
  const [notify, setNotify] = useState(profile.notify_user_ids.join(', '));
  const [reason, setReason] = useState('');

  const currencyError = currency && !/^[A-Za-z]{3}$/.test(currency) ? 'ISO-4217, e.g. EGP.' : null;
  const taxBps = taxPercent.trim() === '' ? null : Math.round(Number(taxPercent) * 100);
  const taxError = taxPercent.trim() !== '' && (!Number.isFinite(Number(taxPercent)) || Number(taxPercent) < 0 || Number(taxPercent) > 100)
    ? 'A percentage 0–100.' : null;
  let addressValue: unknown = null;
  let addressError: string | null = null;
  if (address.trim()) {
    try {
      addressValue = JSON.parse(address);
    } catch {
      addressError = 'Address must be JSON (e.g. {"line1": "…", "city": "Cairo", "country": "EG"}).';
    }
  }
  const termsNumber = Number(terms);
  const termsError = !Number.isInteger(termsNumber) || termsNumber < 0 || termsNumber > 365 ? 'Whole days, 0 to 365.' : null;
  const notifyIds = notify.split(/[\s,]+/).map((v) => v.trim()).filter(Boolean);
  const notifyError = notifyIds.some((id) => !/^[0-9a-f-]{36}$/i.test(id)) ? 'User UUIDs, comma separated.' : null;

  // A money-relevant change (currency / tax rate / tax exemption) needs a reason; billing refuses it otherwise.
  const moneyChanged =
    (currency.toUpperCase() || null) !== (profile.currency ?? null) ||
    taxBps !== profile.tax_rate_bps ||
    (exemptReason.trim() || null) !== (profile.tax_exempt_reason ?? null);
  const valid = !currencyError && !taxError && !addressError && !termsError && !notifyError && (!moneyChanged || reasonOk(reason));

  const save = useMutation({
    mutationFn: () =>
      billingApi.updateProfile(env, tenantId, {
        // Full replacement: a null clears the override and falls back to the platform default.
        currency: currency.trim() ? currency.trim().toUpperCase() : null,
        tax_rate_bps: taxBps,
        tax_exempt_reason: exemptReason.trim() || null,
        tax_registration_id: taxId.trim() || null,
        legal_name: legalName.trim() || null,
        address: addressValue,
        invoice_language: language,
        payment_terms_days: termsNumber,
        notify_user_ids: notifyIds,
        reason: reason.trim() || null,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(key(env, tenantId, 'profile'), updated);
      onClose();
    },
  });

  return (
    <Modal title="Edit billing profile" size="lg" onClose={save.isPending ? undefined : onClose} dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save profile'}
          </button>
        </>
      }>
      <p className="t-caption muted" style={{ margin: 0 }}>
        Empty = use the platform default. Issued invoices keep the currency and tax they were issued with.
      </p>
      <div className="form-grid">
        <TextField label="Currency override" value={currency} onChange={setCurrency} mono error={currencyError} placeholder={profile.effective_currency} />
        <TextField label="Tax rate override (%)" value={taxPercent} onChange={setTaxPercent} type="number" min={0} max={100} step="any" error={taxError}
          hint="0 for exempt / export." />
        <TextField label="Tax exempt reason" value={exemptReason} onChange={setExemptReason} />
        <TextField label="Tax registration id" value={taxId} onChange={setTaxId} mono />
        <TextField label="Legal name" value={legalName} onChange={setLegalName} />
        <TextField label="Payment terms (days)" value={terms} onChange={setTerms} type="number" min={0} error={termsError} />
      </div>
      <Segmented label="Invoice language" value={language} onChange={setLanguage} options={[{ value: 'en', label: 'English' }, { value: 'ar', label: 'Arabic' }]} />
      <TextAreaField label="Legal address (JSON)" value={address} onChange={setAddress} mono rows={4} error={addressError} />
      <TextField label="Notify users (UUIDs)" value={notify} onChange={setNotify} mono error={notifyError} />
      <ReasonField value={reason} onChange={setReason}
        label={moneyChanged ? 'Reason (required — the currency or tax changes)' : 'Reason (optional)'}
        showError={moneyChanged && reason.length > 0} />
      <MutationError error={save.error} service="billing" />
    </Modal>
  );
}

// ─── Price overrides ──────────────────────────────────────────────────────

function OverridesSection({ env, tenantId }: { env: string; tenantId: string }) {
  const queryClient = useQueryClient();
  const overrides = useQuery({ queryKey: key(env, tenantId, 'overrides'), queryFn: () => billingApi.overrides(env, tenantId), ...LIVE });
  const { scale } = useCreditScale(env);
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const [creating, setCreating] = useState(false);
  const [expiring, setExpiring] = useState<PriceOverride | null>(null);
  const [reason, setReason] = useState('');

  const expire = useMutation({
    mutationFn: () => billingApi.expireOverride(env, expiring!.id, reason.trim()),
    onSuccess: () => {
      setExpiring(null);
      setReason('');
      queryClient.invalidateQueries({ queryKey: key(env, tenantId, 'overrides') });
    },
  });

  return (
    <Section title="Price overrides" meta="a negotiated price for this tenant beats the book while valid"
      actions={canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreating(true)}>Add override</button>} flush>
      <QueryState query={overrides} service="billing">
        {(data) => data.overrides.length === 0 ? (
          <div className="empty-state t-body">No overrides — this tenant pays the price book.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>SKU</th><th className="num">Price</th><th>Validity</th><th>Reason</th><th>State</th>{canManage && <th aria-label="Actions" />}</tr>
              </thead>
              <tbody>
                {data.overrides.map((o) => (
                  <tr key={o.id} className={o.active ? undefined : 'row-muted'}>
                    <td className="mono">{o.sku}</td>
                    <td className="num">
                      {o.markup_bps !== null
                        ? `cost + ${formatBps(o.markup_bps)}`
                        : o.currency === 'CREDITS'
                          ? formatCredits(o.amount, scale)
                          : formatMinor(o.amount, o.currency)}
                    </td>
                    <td className="t-caption">{formatTimestamp(o.starts_at)} → {o.expires_at ? formatTimestamp(o.expires_at) : 'no expiry'}</td>
                    <td className="t-caption wrap">{o.reason}<div className="muted">{o.created_by ?? ''}</div></td>
                    <td>{o.active ? <StatusChip status="active" /> : <StatusChip status="expired" title={o.expired_at ? formatTimestamp(o.expired_at) : undefined} />}</td>
                    {canManage && (
                      <td>{o.active && <button type="button" className="btn btn-ghost btn-sm is-danger" onClick={() => setExpiring(o)}>Expire</button>}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>

      {creating && <OverrideForm env={env} tenantId={tenantId} onClose={() => setCreating(false)} />}
      {expiring && (
        <ConfirmDialog
          title={`Expire the override on ${expiring.sku}?`}
          confirmLabel="Expire override"
          destructive
          pending={expire.isPending}
          confirmDisabled={!reasonOk(reason)}
          onConfirm={() => expire.mutate()}
          onCancel={() => { setExpiring(null); setReason(''); expire.reset(); }}
        >
          <p className="t-body" style={{ margin: 0 }}>From now on the tenant pays the price book (or an offer) for this SKU. The row stays on the trail.</p>
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={expire.error} service="billing" />
        </ConfirmDialog>
      )}
    </Section>
  );
}

export function OverrideForm({ env, tenantId, onClose }: { env: string; tenantId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { scale, status } = useCreditScale(env);
  // A message price (picked, CREDITS) or any other SKU (typed — a plan, a pack, an overage).
  const [target, setTarget] = useState<'message' | 'sku'>('message');
  const [family, setFamily] = useState<MessageSkuFamily>('whatsapp');
  const [messageType, setMessageType] = useState<string>('marketing');
  const [country, setCountry] = useState('*');
  const [freeSku, setFreeSku] = useState('');
  const [freeCurrency, setFreeCurrency] = useState('EGP');
  const [mode, setMode] = useState<'amount' | 'markup'>('amount');
  const [amount, setAmount] = useState('');
  const [markupPercent, setMarkupPercent] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [reason, setReason] = useState('');

  const pickedSku = target === 'message' ? messageSku(family, messageType, country) : null;
  const sku = target === 'message' ? pickedSku ?? '' : freeSku;
  // Every message price is a consumption rate: its override is in CREDITS, whatever was typed before.
  const currency = target === 'message' ? 'CREDITS' : freeCurrency;
  const isCredits = currency.toUpperCase() === 'CREDITS';
  const countryError = target === 'message' && pickedSku === null ? 'Two letters (ISO 3166), or * for every country.' : null;
  const effectiveMode = isCredits ? mode : 'amount';
  const parsed = effectiveMode === 'amount'
    ? isCredits ? parseDisplayedCredits(amount, scale) : parseMajorToMinor(amount, currency.toUpperCase())
    : null;
  const markupBps = Math.round(Number(markupPercent) * 100);
  const markupError = effectiveMode === 'markup' && (!markupPercent.trim() || !Number.isFinite(markupBps) || markupBps < 0) ? 'A non-negative percentage.' : null;
  const currencyError = !isCredits && !/^[A-Za-z]{3}$/.test(currency) ? 'ISO-4217 or CREDITS.' : null;

  const valid = sku.trim() !== '' && !currencyError && reasonOk(reason) &&
    (effectiveMode === 'markup' ? !markupError : !!parsed && parsed.ok);

  const create = useMutation({
    mutationFn: () => billingApi.createOverride(env, {
      tenant_id: tenantId,
      sku: sku.trim(),
      currency: currency.toUpperCase(),
      amount: effectiveMode === 'amount' && parsed?.ok ? ('micro' in parsed ? parsed.micro : parsed.minor) : null,
      markup_bps: effectiveMode === 'markup' ? markupBps : null,
      reason: reason.trim(),
      starts_at: startsAt ? new Date(startsAt).toISOString() : undefined,
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key(env, tenantId, 'overrides') });
      onClose();
    },
  });

  return (
    <Modal title="Add a price override" onClose={create.isPending ? undefined : onClose} dismissible={!create.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={create.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Saving…' : 'Add override'}
          </button>
        </>
      }>
      <Segmented label="Price of" value={target} onChange={setTarget}
        options={[{ value: 'message', label: 'A message type' }, { value: 'sku', label: 'Another SKU' }]} />
      {target === 'message' ? (
        <div className="form-grid">
          <Select label="Product" value={family} searchable={false}
            onChange={(v) => { const next = v as MessageSkuFamily; setFamily(next); setMessageType(MESSAGE_SKU_FAMILIES[next].types[0]); }}
            options={(Object.keys(MESSAGE_SKU_FAMILIES) as MessageSkuFamily[]).map((f) => ({ value: f, label: MESSAGE_SKU_FAMILIES[f].label }))} />
          <Select label={family === 'sms' ? 'Purpose' : family === 'verify' ? 'Channel' : 'Category'} value={messageType} searchable={false}
            onChange={setMessageType} options={MESSAGE_SKU_FAMILIES[family].types.map((t) => ({ value: t, label: t }))} />
          <TextField label="Country" value={country} onChange={setCountry} mono error={countryError} hint="ISO-2, or * for every country." />
          <div className="field">
            <span className="field-label t-label">SKU · currency</span>
            <span className="mono t-body">{pickedSku ?? '—'} · CREDITS</span>
          </div>
        </div>
      ) : (
        <div className="form-grid">
          <TextField label="SKU" value={freeSku} onChange={setFreeSku} mono required placeholder="plan.pro.monthly" />
          <TextField label="Currency" value={freeCurrency} onChange={setFreeCurrency} mono error={currencyError}
            hint="An ISO code, or CREDITS for a consumption rate." />
        </div>
      )}
      {isCredits && (
        <Segmented label="Price as" value={mode} onChange={setMode}
          options={[{ value: 'amount', label: 'Explicit credits' }, { value: 'markup', label: 'Cost + markup' }]} />
      )}
      {effectiveMode === 'amount' ? (
        <TextField
          label={isCredits ? 'Price (displayed credits per unit)' : `Price (${currency.toUpperCase()})`}
          value={amount}
          onChange={setAmount}
          error={amount && parsed && !parsed.ok ? parsed.error : null}
          disabled={isCredits && status !== 'ok'}
          hint={isCredits && status !== 'ok' ? scaleUnavailableReason(status) : isCredits ? `Sent as micro-credits (× ${scaleText(scale)}).` : 'Ex tax.'}
        />
      ) : (
        <TextField label="Markup over cost (%)" value={markupPercent} onChange={setMarkupPercent} type="number" min={0} step="any" error={markupError} />
      )}
      <div className="form-grid">
        <TextField label="Starts (your local time)" value={startsAt} onChange={setStartsAt} type="datetime-local" hint="Empty = now." />
        <TextField label="Expires (your local time)" value={expiresAt} onChange={setExpiresAt} type="datetime-local" hint="Empty = no expiry." />
      </div>
      <ReasonField value={reason} onChange={setReason} />
      <MutationError error={create.error} service="billing" />
    </Modal>
  );
}

// ─── Invoices ─────────────────────────────────────────────────────────────

type InvoiceAction = 'issue' | 'void' | 'mark-paid';

function InvoicesSection({ env, tenantId }: { env: string; tenantId: string }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const [action, setAction] = useState<{ invoice: Invoice; kind: InvoiceAction } | null>(null);
  const [reason, setReason] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [link, setLink] = useState<{ invoiceId: string; url: string } | null>(null);
  const [generating, setGenerating] = useState(false);

  const invoices = useInfiniteQuery({
    queryKey: key(env, tenantId, 'invoices'),
    queryFn: ({ pageParam }) => billingApi.invoices(env, { tenant_id: tenantId, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    ...LIVE,
  });

  const act = useMutation({
    mutationFn: () => {
      const r = reason.trim();
      const id = action!.invoice.id;
      if (action!.kind === 'issue') return billingApi.issueInvoice(env, id, r);
      if (action!.kind === 'void') return billingApi.voidInvoice(env, id, r);
      return billingApi.markInvoicePaid(env, id, r);
    },
    onSuccess: () => {
      setAction(null);
      setReason('');
      queryClient.invalidateQueries({ queryKey: key(env, tenantId, 'invoices') });
    },
  });

  const pdf = useMutation({
    mutationFn: (invoice: Invoice) => billingApi.invoicePdf(env, invoice.id, `${invoice.number ?? invoice.id}.pdf`),
  });

  const paymentLink = useMutation({
    mutationFn: (invoice: Invoice) => billingApi.invoicePaymentLink(env, invoice.id),
    onSuccess: (result) => setLink({ invoiceId: result.invoice_id, url: result.checkout_url }),
    gcTime: 0,
  });

  const pages = invoices.data?.pages ?? [];
  const rows = pages.flatMap((p) => p.invoices);

  return (
    <Section title="Invoices" meta="live from billing"
      actions={canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setGenerating(true)}>Generate for a period</button>} flush>
      <QueryState query={{ ...invoices, data: invoices.data ? rows : undefined }} service="billing">
        {(list) => list.length === 0 ? (
          <div className="empty-state t-body">No invoices for this tenant.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Invoice</th><th>Status</th><th className="num">Total</th><th>Dates</th><th aria-label="Actions" /></tr>
              </thead>
              <tbody>
                {list.map((inv) => (
                  <InvoiceRow
                    key={inv.id}
                    env={env}
                    invoice={inv}
                    open={openId === inv.id}
                    onToggle={() => setOpenId(openId === inv.id ? null : inv.id)}
                    canManage={canManage}
                    onAction={(kind) => setAction({ invoice: inv, kind })}
                    onPdf={() => pdf.mutate(inv)}
                    pdfPending={pdf.isPending && pdf.variables?.id === inv.id}
                    onPaymentLink={() => paymentLink.mutate(inv)}
                    linkPending={paymentLink.isPending && paymentLink.variables?.id === inv.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      <LoadMore hasMore={!!invoices.hasNextPage} loading={invoices.isFetchingNextPage} onLoad={() => invoices.fetchNextPage()} />
      <div className="card-body stack-sm" style={{ paddingTop: 0 }}>
        <MutationError error={pdf.error} service="billing" />
        <MutationError error={paymentLink.error} service="billing" />
        {link && (
          <div className="callout callout-info" role="status">
            <span className="callout-icon" aria-hidden="true">i</span>
            <div className="t-body" style={{ minWidth: 0 }}>
              <strong className="t-heading" style={{ display: 'block' }}>Payment link for invoice {shortId(link.invoiceId)}</strong>
              <a href={link.url} target="_blank" rel="noreferrer noopener" className="mono t-caption" style={{ overflowWrap: 'anywhere' }}>{link.url}</a>
              <button type="button" className="btn btn-ghost btn-sm" style={{ marginInlineStart: 8 }} onClick={() => setLink(null)}>Dismiss</button>
            </div>
          </div>
        )}
      </div>

      {action && (
        <ConfirmDialog
          title={`${action.kind === 'mark-paid' ? 'Mark paid' : action.kind === 'issue' ? 'Issue' : 'Void'} invoice ${action.invoice.number ?? shortId(action.invoice.id)}?`}
          confirmLabel={action.kind === 'mark-paid' ? 'Mark paid' : action.kind === 'issue' ? 'Issue invoice' : 'Void invoice'}
          destructive={action.kind === 'void'}
          pending={act.isPending}
          confirmDisabled={!reasonOk(reason)}
          onConfirm={() => act.mutate()}
          onCancel={() => { setAction(null); setReason(''); act.reset(); }}
        >
          <p className="t-body" style={{ margin: 0 }}>
            {action.kind === 'issue' && 'Issuing freezes the lines, the currency and the tax snapshot and assigns the invoice number.'}
            {action.kind === 'void' && 'A void invoice stays on the record (invoices are never deleted) but is no longer owed.'}
            {action.kind === 'mark-paid' && 'Records a payment received outside the gateways (bank transfer, cheque). No money moves.'}
            {' '}Total {formatMinor(action.invoice.total_minor, action.invoice.currency)}.
          </p>
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={act.error} service="billing" />
        </ConfirmDialog>
      )}
      {generating && <GenerateInvoicesForm env={env} tenantId={tenantId} onClose={() => setGenerating(false)} />}
    </Section>
  );
}

function InvoiceRow({
  env, invoice: inv, open, onToggle, canManage, onAction, onPdf, pdfPending, onPaymentLink, linkPending,
}: {
  env: string;
  invoice: Invoice;
  open: boolean;
  onToggle: () => void;
  canManage: boolean;
  onAction: (kind: InvoiceAction) => void;
  onPdf: () => void;
  pdfPending: boolean;
  onPaymentLink: () => void;
  linkPending: boolean;
}) {
  const payable = inv.status === 'issued' || inv.status === 'overdue';
  return (
    <>
      <tr>
        <td>
          <button type="button" className="linklike" onClick={onToggle} aria-expanded={open}>{inv.number ?? '(draft)'}</button>
          <div className="t-caption muted">{inv.kind}{inv.period ? ` · ${inv.period}` : ''} · <span className="mono">{shortId(inv.id)}</span></div>
        </td>
        <td><StatusChip status={inv.status} /></td>
        <td className="num">
          {formatMinor(inv.total_minor, inv.currency)}
          <div className="t-caption muted">tax {formatBps(inv.tax_rate_bps)} {formatMinor(inv.tax_minor, inv.currency)}</div>
        </td>
        <td className="t-caption">
          {inv.issued_at && <div>issued {formatTimestamp(inv.issued_at)}</div>}
          {inv.due_at && <div>due {formatTimestamp(inv.due_at)}</div>}
          {inv.paid_at && <div>paid {formatTimestamp(inv.paid_at)}</div>}
          {inv.voided_at && <div>void {formatTimestamp(inv.voided_at)}</div>}
          {!inv.issued_at && <div className="muted">created {formatTimestamp(inv.created_at)}</div>}
        </td>
        <td>
          <div className="row">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onPdf} disabled={pdfPending}>{pdfPending ? 'Preparing…' : 'PDF'}</button>
            {canManage && inv.status === 'draft' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => onAction('issue')}>Issue</button>}
            {canManage && payable && <button type="button" className="btn btn-ghost btn-sm" onClick={() => onAction('mark-paid')}>Mark paid</button>}
            {canManage && payable && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={onPaymentLink} disabled={linkPending}>
                {linkPending ? 'Creating…' : 'Payment link'}
              </button>
            )}
            {canManage && inv.status !== 'void' && inv.status !== 'paid' && (
              <button type="button" className="btn btn-ghost btn-sm is-danger" onClick={() => onAction('void')}>Void</button>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5}>
            <InvoiceLines env={env} invoiceId={inv.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function InvoiceLines({ env, invoiceId }: { env: string; invoiceId: string }) {
  const detail = useQuery({ queryKey: ['ops', 'billing', env, 'invoice', invoiceId], queryFn: () => billingApi.invoice(env, invoiceId), ...LIVE });
  return (
    <QueryState query={detail} service="billing">
      {(d) => (
        <table className="table">
          <thead><tr><th>#</th><th>Line</th><th className="num">Qty</th><th className="num">Unit</th><th className="num">Discount</th><th className="num">Amount</th><th>Priced from</th></tr></thead>
          <tbody>
            {d.lines.map((l) => (
              <tr key={l.line_no}>
                <td>{l.line_no}</td>
                <td>{l.description_en ?? l.sku}<div className="t-caption muted mono">{l.sku}</div></td>
                <td className="num">{l.quantity}</td>
                <td className="num">{formatMinor(l.unit_amount_minor, d.invoice.currency)}</td>
                <td className="num">{formatMinor(l.discount_minor, d.invoice.currency)}</td>
                <td className="num">{formatMinor(l.amount_minor, d.invoice.currency)}</td>
                <td className="t-caption wrap">{l.explanation ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </QueryState>
  );
}

function GenerateInvoicesForm({ env, tenantId, onClose }: { env: string; tenantId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState(currentPeriod());
  const [reason, setReason] = useState('');
  const generate = useMutation({
    mutationFn: () =>
      // Deterministic invoice ids: a re-run for the same period is a no-op on billing's side.
      billingApi.generateInvoices(env, { period, tenant_id: tenantId, reason: reason.trim() }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key(env, tenantId, 'invoices') }),
  });

  return (
    <Modal title="Generate invoices" onClose={generate.isPending ? undefined : onClose} dismissible={!generate.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={generate.isPending}>Close</button>
          <button type="button" className="btn btn-primary" disabled={!reasonOk(reason) || generate.isPending} onClick={() => generate.mutate()}>
            {generate.isPending ? 'Generating…' : 'Generate'}
          </button>
        </>
      }>
      <MonthSelect value={period} onChange={setPeriod} label="Period" />
      <p className="t-caption muted" style={{ margin: 0 }}>For this tenant only. Invoice ids are deterministic — generating twice creates nothing new.</p>
      <ReasonField value={reason} onChange={setReason} />
      <MutationError error={generate.error} service="billing" />
      {generate.data && (
        <div className="callout callout-info" role="status">
          <span className="callout-icon" aria-hidden="true">i</span>
          <div className="t-body">{generate.data.created} created · {generate.data.existing} already existed · {generate.data.skipped} skipped</div>
        </div>
      )}
    </Modal>
  );
}

// ─── Purchases (+ retry of a parked grant) ─────────────────────────────────

/** A custom-amount purchase is priced from the `credits.unit.<wallet>` row (2026-10-14), not a pack. */
export function isCustomPurchase(purchase: Pick<Purchase, 'pack_sku'>): boolean {
  return purchase.pack_sku.startsWith('credits.unit.');
}

export function PurchasesSection({ env, tenantId }: { env: string; tenantId: string }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const { scale } = useCreditScale(env);
  const [retrying, setRetrying] = useState<Purchase | null>(null);
  const [reason, setReason] = useState('');

  const purchases = useInfiniteQuery({
    queryKey: key(env, tenantId, 'purchases'),
    queryFn: ({ pageParam }) => billingApi.purchases(env, { tenant_id: tenantId, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    ...LIVE,
  });

  const retry = useMutation({
    mutationFn: () => billingApi.retryGrant(env, retrying!.id, reason.trim()),
    onSuccess: () => {
      setRetrying(null);
      setReason('');
      queryClient.invalidateQueries({ queryKey: key(env, tenantId, 'purchases') });
    },
  });

  const rows = purchases.data?.pages.flatMap((p) => p.purchases) ?? [];

  return (
    <Section title="Credit purchases" meta="a paid purchase whose credits could not be added is parked here, never lost" flush>
      <QueryState query={{ ...purchases, data: purchases.data ? rows : undefined }} service="billing">
        {(list) => {
          // The fee column appears once any row carries one; older purchases (before the fee) show a dash, never 0.
          const showFee = list.some((p) => p.service_fee_minor !== null && p.service_fee_minor !== undefined);
          return list.length === 0 ? (
          <div className="empty-state t-body">No purchases.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Pack</th><th className="num">Credits</th>{showFee && <th className="num">Service fee</th>}<th className="num">Total</th><th>Status</th><th>Grant</th>{canManage && <th aria-label="Actions" />}</tr></thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {isCustomPurchase(p)
                        ? <><strong>Custom amount</strong> <span className="mono t-caption muted">{p.pack_sku}</span></>
                        : <span className="mono">{p.pack_sku}</span>}
                      <div className="t-caption muted">{p.wallet} · {p.channel} · {formatTimestamp(p.created_at)}</div>
                    </td>
                    <td className="num">
                      {/* credits_display is billing's own display string for this row. */}
                      {p.credits_display} credits
                      {p.bonus_credits > 0 && <div className="t-caption muted">+ bonus {formatCredits(p.bonus_credits, scale)}</div>}
                    </td>
                    {showFee && (
                      <td className="num">
                        {p.service_fee_minor === null || p.service_fee_minor === undefined ? <span className="subtle" title="Bought before the service fee existed">—</span> : (
                          <>
                            {formatMinor(p.service_fee_minor, p.currency)}
                            {typeof p.service_fee_bps === 'number' && <div className="t-caption muted">{formatBps(p.service_fee_bps)}</div>}
                          </>
                        )}
                      </td>
                    )}
                    <td className="num">{formatMinor(p.total_minor, p.currency)}<div className="t-caption muted">incl. tax {formatMinor(p.tax_minor, p.currency)}</div></td>
                    <td><StatusChip status={p.status} /></td>
                    <td className="t-caption">
                      {p.granted_at ? `granted ${formatTimestamp(p.granted_at)}` : `${p.grant_attempts} attempt(s)`}
                      {p.next_grant_attempt_at && <div className="muted">next {formatTimestamp(p.next_grant_attempt_at)}</div>}
                      {p.last_grant_error && <div className="note-error wrap">{p.last_grant_error}</div>}
                    </td>
                    {canManage && (
                      <td>{p.status === 'grant_failed' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRetrying(p)}>Retry grant</button>}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        }}
      </QueryState>
      <LoadMore hasMore={!!purchases.hasNextPage} loading={purchases.isFetchingNextPage} onLoad={() => purchases.fetchNextPage()} />
      {retrying && (
        <ConfirmDialog
          title={`Retry the grant of ${retrying.pack_sku}?`}
          confirmLabel="Retry grant"
          pending={retry.isPending}
          confirmDisabled={!reasonOk(reason)}
          onConfirm={() => retry.mutate()}
          onCancel={() => { setRetrying(null); setReason(''); retry.reset(); }}
        >
          <p className="t-body" style={{ margin: 0 }}>
            Re-queues the grant with the SAME idempotency key, so the wallet applies it at most once.
          </p>
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={retry.error} service="billing" />
        </ConfirmDialog>
      )}
    </Section>
  );
}

// ─── Payments ─────────────────────────────────────────────────────────────

function PaymentsSection({ env, tenantId }: { env: string; tenantId: string }) {
  const payments = useInfiniteQuery({
    queryKey: key(env, tenantId, 'payments'),
    queryFn: ({ pageParam }) => billingApi.payments(env, { tenant_id: tenantId, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    ...LIVE,
  });
  const rows = payments.data?.pages.flatMap((p) => p.payments) ?? [];

  return (
    <Section title="Payments" meta="checkouts through the gateways" flush>
      <QueryState query={{ ...payments, data: payments.data ? rows : undefined }} service="billing">
        {(list) => list.length === 0 ? (
          <div className="empty-state t-body">No payments.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Payment</th><th>Purpose</th><th className="num">Amount</th><th>Status</th><th>Gateway</th><th>Dates</th></tr></thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id}>
                    <td className="mono t-caption">{shortId(p.id)}{p.external_id && <div className="muted">ext {p.external_id}</div>}</td>
                    <td className="t-caption">{p.purpose}<div className="muted">{p.scope}</div></td>
                    <td className="num">{formatMinor(p.amount_minor, p.currency)}{p.refunded_minor > 0 && <div className="t-caption muted">refunded {formatMinor(p.refunded_minor, p.currency)}</div>}</td>
                    <td><StatusChip status={p.status} />{p.failure_reason && <div className="t-caption note-error">{p.failure_reason}</div>}</td>
                    <td className="t-caption mono">{p.gateway_key}{p.method && <div className="muted">{p.method}</div>}</td>
                    <td className="t-caption">{formatTimestamp(p.created_at)}{p.settled_at && <div className="muted">settled {formatTimestamp(p.settled_at)}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      <LoadMore hasMore={!!payments.hasNextPage} loading={payments.isFetchingNextPage} onLoad={() => payments.fetchNextPage()} />
    </Section>
  );
}

// ─── Manual grant ─────────────────────────────────────────────────────────

function ManualGrantSection({ env, tenantId }: { env: string; tenantId: string }) {
  if (!hasPermission(PERMISSIONS.billingManage)) return null;
  return <ManualGrantForm env={env} tenantId={tenantId} />;
}

function ManualGrantForm({ env, tenantId }: { env: string; tenantId: string }) {
  const { scale, status } = useCreditScale(env);
  const [wallet, setWallet] = useState<'ai' | 'communications'>('communications');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  // One key per intended grant: a retry of the SAME form re-sends it, so billing applies it once.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [result, setResult] = useState<ManualGrantResult | null>(null);
  const [confirming, setConfirming] = useState(false);

  const parsed = parseDisplayedCredits(amount, scale);
  const scaleReason = scaleUnavailableReason(status);
  const valid = status === 'ok' && parsed.ok && parsed.micro > 0 && reasonOk(reason);

  const reKey = () => setIdempotencyKey(newIdempotencyKey());

  const grant = useMutation({
    mutationFn: () => billingApi.grant(env, {
      tenant_id: tenantId,
      wallet,
      credits: parsed.ok ? parsed.micro : 0,
      reason: reason.trim(),
      idempotency_key: idempotencyKey,
    }),
    onSuccess: (r) => {
      setResult(r);
      setConfirming(false);
      setAmount('');
      setReason('');
      reKey();
    },
    gcTime: 0,
  });

  return (
    <Section title="Manual grant" meta="a live, idempotent grant into the tenant's wallet">
      <div className="form-grid">
        <Segmented label="Wallet" value={wallet} onChange={(w) => { setWallet(w); reKey(); }}
          options={[{ value: 'communications', label: 'Communications (SMS + WhatsApp)' }, { value: 'ai', label: 'AI' }]} />
        <TextField
          label="Credits (displayed)"
          value={amount}
          onChange={(v) => { setAmount(v); reKey(); }}
          disabled={status !== 'ok'}
          error={amount && !parsed.ok && status === 'ok' ? parsed.error : null}
          hint={scaleReason ?? (parsed.ok ? `Sent as ${parsed.micro.toLocaleString('en-US')} micro-credits (1 credit = ${scale} micro-credits).` : undefined)}
        />
      </div>
      <ReasonField value={reason} onChange={setReason} />
      <div className="form-actions">
        <button type="button" className="btn btn-primary" disabled={!valid || grant.isPending} onClick={() => setConfirming(true)}>Grant credits</button>
        <span className="t-caption subtle">idempotency key <span className="mono">{idempotencyKey.slice(0, 8)}…</span></span>
      </div>
      {!confirming && <MutationError error={grant.error} service="billing" />}
      {result && (
        <div className="callout callout-info" role="status" style={{ marginTop: 12 }}>
          <span className="callout-icon" aria-hidden="true">i</span>
          <div className="t-body">
            <strong className="t-heading" style={{ display: 'block' }}>
              {result.already_applied ? 'Already applied — nothing new was granted' : 'Granted'}
            </strong>
            {formatCredits(result.amount_granted_credits, scale)} into <span className="mono">{result.wallet}</span>. Wallet balance now{' '}
            <CreditAmount micro={result.balance_credits} scale={scale} inline /> (the wallet's own answer).
          </div>
        </div>
      )}
      {confirming && parsed.ok && (
        <ConfirmDialog
          title="Grant credits?"
          confirmLabel="Grant"
          pending={grant.isPending}
          onConfirm={() => grant.mutate()}
          onCancel={() => { setConfirming(false); grant.reset(); }}
        >
          <p className="t-body" style={{ margin: 0 }}>
            {formatCredits(parsed.micro, scale)} ({parsed.micro.toLocaleString('en-US')} micro-credits) into the <strong>{wallet}</strong> wallet
            of tenant <span className="mono">{shortId(tenantId)}</span> on <span className="mono">{env}</span>. Reason: “{reason.trim()}”.
          </p>
          <MutationError error={grant.error} service="billing" />
        </ConfirmDialog>
      )}
    </Section>
  );
}

